import { Divider, FormInstance } from 'antd/lib'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { PromptGenerationFormType, PromptGenerationFromContextFormType } from 'Types/Tasks.types'
import classes from './PlagiarismChecker.module.less'
import { checkForPlagiarismApi, PlagiarismCheckResponseType } from 'HTTPClients/plugins.client'
import { PlagiarismPlugin } from 'Plugins/Plagiarism/Plagiarism.plugin'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
// @ts-ignore
import h2p from 'html2plaintext'
import CloseOutlined from '@ant-design/icons/lib/icons/CloseOutlined'
import { CloseCircleFilled } from '@ant-design/icons'
import PluginOkayIcon from 'Assets/icons/plugin-okay.svg'
import { Loader } from 'Components/common/Loader/Loader'
import Form from 'antd/lib/form'
import { useMessage } from 'shared/hooks/use-message'

type PlagiarismCheckerPropsType = {
    installedPlugins: IPlugin[]
    form: FormInstance<PromptGenerationFormType> | FormInstance<PromptGenerationFromContextFormType>
    onPlagiarismTestResult: (val: boolean) => void
    onPlagiarismScan?: () => void
}

export const PlagiarismChecker = (props: PlagiarismCheckerPropsType) => {
    const { installedPlugins, form, onPlagiarismTestResult } = props
    const prompt = Form.useWatch('prompt', form)

    const [isLoadingPlagiarismDetails, setIsLoadingPlagiarismDetails] = useState(false)
    const [plagiarsmDetails, setPlagiarismDetails] = useState<PlagiarismCheckResponseType | null>(
        null
    )
    const [isCheckForPlagiarismDisabled, setIsCheckForPlagiarismDisabled] = useState(false)
    const [messageApi] = useMessage()

    const plagiarismPlugin = useMemo(() => {
        return installedPlugins.find(plugin => plugin.id === 'Plagiarism') as PlagiarismPlugin
    }, [installedPlugins])

    useEffect(() => {
        if (plagiarismPlugin) {
            setIsCheckForPlagiarismDisabled(false)
            onPlagiarismTestResult(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prompt])

    const fetchPlagiarismDetails = useCallback(async () => {
        props.onPlagiarismScan?.()
        const pluginDetails = plagiarismPlugin.getDetails()

        const text = h2p(convertMarkdownToHTML(prompt as string)) as string
        if (!text) {
            messageApi.error(
                `Question length is less than the minimum word limit of ${pluginDetails.config.minWordLimit}`
            )
            return
        }

        const words = text.split(' ').filter(w => !!w.trim()).length

        if (pluginDetails.config.minWordLimit > 0 && words < pluginDetails.config.minWordLimit) {
            messageApi.error(
                `Question length is less than the minimum word limit of ${pluginDetails.config.minWordLimit}`
            )
            return
        }

        if (pluginDetails.config.maxWordsLimit > 0 && pluginDetails.config.maxWordsLimit < words) {
            messageApi.error(
                `Question length is greater than the maximum word limit of ${pluginDetails.config.maxWordsLimit}`
            )
            return
        }

        setIsLoadingPlagiarismDetails(true)
        setPlagiarismDetails(null)

        const resp = await checkForPlagiarismApi({
            taskPluginId: plagiarismPlugin.getDetails().id,
            text,
        })

        if (resp.isErr()) {
            if (
                (resp.error.response?.data as { error: { message: string } })?.error?.message ===
                'plugin usage limit exhausted.'
            ) {
                setIsCheckForPlagiarismDisabled(true)
                onPlagiarismTestResult(true)
                messageApi.error(`You have reached the maximum limit for plagiarism checks.`)
            } else {
                messageApi.error(`Failed to check for plagiarism. Please try again.`)
            }
            setIsLoadingPlagiarismDetails(false)

            return
        }

        setPlagiarismDetails(resp.value)
        setIsLoadingPlagiarismDetails(false)
    }, [props, plagiarismPlugin, prompt, onPlagiarismTestResult, messageApi])

    const onClickOkayButton = useCallback(() => {
        setPlagiarismDetails(null)
        setIsCheckForPlagiarismDisabled(true)
        onPlagiarismTestResult(true)
    }, [onPlagiarismTestResult])

    const loadingContent = useMemo(() => {
        return <Loader title="Scanning... Please wait..." />
    }, [])

    const plagiarismContentSummary = useMemo(() => {
        if (!plagiarsmDetails) {
            return null
        }

        const pluginDetails = plagiarismPlugin.getDetails()
        const { plag_percent } = plagiarsmDetails

        if (plag_percent > pluginDetails.config.maximumPercentPlagiarismAllowed) {
            return (
                <div className={classes.plagiarismSummary}>
                    <CloseCircleFilled className={classes.plagiarismIcon} />
                    <span className={classes.plagiarismText}>
                        Your question didn’t pass our criteria for testing model response. Please
                        edit the question and retry
                    </span>
                    <Divider className={classes.divider} />
                    <span className={classes.pluginResultTitle}>Plugin result</span>
                    <span className={classes.pluginResult}>
                        <span>{plag_percent}%</span> Plagiairsm detected
                    </span>
                </div>
            )
        }

        return (
            <div className={classes.noPlagiarismSummary}>
                <img src={PluginOkayIcon} alt={'No Plagiarism Detected'} />
                <p>Great!!! Your question passed our criteria for testing model response</p>
                <ActionButton title={'Got it'} onClick={onClickOkayButton} />
            </div>
        )
    }, [onClickOkayButton, plagiarismPlugin, plagiarsmDetails])

    const plagiarismItems = useMemo(() => {
        if (
            !plagiarsmDetails ||
            plagiarsmDetails.plag_percent <
                plagiarismPlugin.getDetails().config.maximumPercentPlagiarismAllowed
        ) {
            return
        }

        return (
            <div className={classes.sourceContainer}>
                {plagiarsmDetails.sources.map((source, i) => {
                    return (
                        <div key={source.url} className={classes.source}>
                            <div className={classes.sourceHeader}>
                                <a href={source.url} className={classes.sourceUrl} target="_blank">
                                    {source.url}
                                </a>
                                <div className={classes.sourcePercentage}>
                                    {source.plag_percent}% match
                                </div>
                            </div>
                            <div className={classes.sourceContent}>
                                Matching text #{i + 1} {source.text_matched}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }, [plagiarismPlugin, plagiarsmDetails])

    const plagiarismDetailsContent = useMemo(() => {
        if (isLoadingPlagiarismDetails) {
            return <>{loadingContent}</>
        }

        if (!plagiarsmDetails) {
            return
        }

        return (
            <>
                <div className={classes.popupOverlay} />
                <div className={classes.popupContainer} data-testid="plagiarism-popup">
                    <div className={classes.header}>
                        <span>Plagiarism Checker</span>
                        <CloseOutlined onClick={() => setPlagiarismDetails(null)} />
                    </div>
                    <div className={classes.content}>
                        {plagiarismContentSummary}
                        {plagiarismItems}
                    </div>
                </div>
            </>
        )
    }, [
        isLoadingPlagiarismDetails,
        loadingContent,
        plagiarismContentSummary,
        plagiarismItems,
        plagiarsmDetails,
    ])

    if (!plagiarismPlugin) {
        return
    }

    return (
        <>
            <ActionButton
                color="default"
                onClick={fetchPlagiarismDetails}
                title="Scan for Plagiarism"
                className={classes.scanButton}
                disabled={isCheckForPlagiarismDisabled}
            />
            {plagiarismDetailsContent}
        </>
    )
}
